<template>
  <div
    v-if="loaded"
    class="flex w-full h-full md:overflow-x-hidden"
    :class="[
      homeClasses,
      !isPoseidonTheme ? (user.profile.showBookOnboarding || !hasBooks) && isBooksPage ? 'bg-primary' : 'bg-sidebar' : '',
      { 'flex-col': showUpgradeToProBar }
    ]"
  >
    <UpgradeToProBar
      v-if="showUpgradeToProBar"
      @hide="showUpgradeBar = false"
    />

    <Sidebar
      ref="sidebar"
      :is-show-backdrop="showBackdrop"
      class="transition-left-300"
      :is-poseidon-theme="isPoseidonTheme"
      :is-paper-shape="isPaperShape"
      :class="[
        showSidebar ? 'left-0' : isPaperShape ? '-left-268' : '-left-254',
        { 'pt-60': showUpgradeToProBar && !isMobile },
        {'pt-150': showUpgradeToProBar && isMobile }
      ]"
      @close="toggleSidebar"
    />

    <section
      class="transition-all duration-300 relative flex flex-col"
      :class="[
        {'main-wrapper': isBooksPage},
        showSidebar ? isWeekPlanner ? 'w-full' : 'md:w-calc' : 'left-0 md:left-20 w-full md:w-calc-20',
        projectInstructionsActiveFirstStep && isMobile ? 'left-0' : showSidebar && 'left-268',
        isBookPage && isPaperShape ? 'bg-book px-15 md:pr-20 md:pl-0 xxl:p-0' : '',
        { 'h-calc-150 mt-150': showUpgradeToProBar && isMobile },
        showUpgradeToProBar ? 'h-calc-60' : 'h-full'
      ]"
    >
      <LoadingModule
        :loading="bookLoading && !mainLoading && (isBookPage || isWeekPlanner || $route.name === 'TaskBoard' || $route.name === 'TaskBoardTeam')"
        class="z-50"
        :class="isMobile ? 'fixed' : 'absolute'"
      />

      <LoadingModule :loading="mainLoading" />

      <BooksInstruction v-if="hasBooks" />

      <UpgradeToProOnboardingModal />

      <HeaderLayout
        :is-upgrade-to-pro-bar="showUpgradeToProBar"
        :is-poseidon-theme="isPoseidonTheme"
        @toggleSidebar="toggleSidebar"
      />

      <main
        id="main"
        class="h-full mt-50 md:mt-0"
        :class="[
          {'mt-50': !isWeekPlanner && $route.name !== 'TaskBoard' && $route.name !== 'TaskBoardTeam'},
          ($route.name === 'Book' || $route.name === 'TeamBook' ) && isMobile ? '' : 'overflow-y-auto',
          $route.name === 'Inbox' && 'overflow-y-scroll',
          mainClasses,
          {'hide-scrollbar': isFocusMode && !isMobile && !showSidebar},
          {'max-w-1100 mx-auto w-full rounded-t-9 mt-70' : isBookPage && isPaperShape},
          {'shadow-paperMode' : isBookPage && isPaperShape && !isDarkMode},
          {'fixed left-268': projectInstructionsActiveFirstStep && isMobile && showSidebar},
          {'border-3 border-link-color rounded-8 mt-60': projectInstructionsActiveSecondStep},
          {'bg-sidebar': projectInstructionsActiveSecondStep && isDarkMode},
          {'bg-white': projectInstructionsActiveSecondStep && !isDarkMode},
          {'md:-ml-5 md:pl-5': isPoseidonTheme && isBooksPage}
        ]"
      >
        <router-view />
      </main>
    </section>
    <!-- <WelcomeModal /> -->
  </div>
</template>

<script>
import HeaderLayout from '@/components/layout/HeaderLayout'
import Sidebar from '@/components/layout/Sidebar'
import { mapGetters, mapState, mapMutations, mapActions } from 'vuex'
import { preventBodyScroll, updatePreviousThemesLocalStorage, dispatchChangingDesign } from '@/core/helper-functions'
import localStorageService from '@/core/local-storage'
import { settingsService } from '@/services'
import BooksInstruction from '@/components/instructions/BooksInstruction'
import UpgradeToProOnboardingModal from '@/components/instructions/UpgradeToProOnboardingModal'
import UpgradeToProBar from '@/components/instructions/UpgradeToProBar'
// import WelcomeModal from '@/components/shared/WelcomeModal'
import LoadingModule from '@/components/shared/LoadingModule'

export default {
  components: {
    Sidebar,
    HeaderLayout,
    BooksInstruction,
    UpgradeToProOnboardingModal,
    UpgradeToProBar,
    // WelcomeModal,
    LoadingModule
  },

  data () {
    return {
      loaded: false,
      showUpgradeBar: true
    }
  },

  computed: {
    ...mapGetters(['isMobile', 'isDarkMode', 'hasBooks', 'isTeamAccount']),

    ...mapState({
      token: state => state.auth.token,
      settings: state => state.settings.settings,
      previousTheme: state => state.settings.previousTheme,
      previousDarkTheme: state => state.settings.previousDarkTheme,
      isFocusMode: state => state.settings.isFocusMode,
      showSidebar: state => state.settings.showSidebar,
      bookLoading: state => state.general.bookLoading,
      mainLoading: state => state.general.loading,
      user: state => state.profile.user,
      projectInstructionsActiveStep: state => state.profile.activeStep,
      isPoseidonTheme: state => state.settings.settings.theme === 'Poseidon',
      isAutoSwitchingThemeEnable: state => state.settings.isAutoSwitchingThemeEnable,
      team: state => state.team.team
    }),

    showUpgradeToProBar () {
      return this.user?.profile.showUpgradeToPro <= 5 &&
        this.user?.profile.showBookOnboarding &&
        this.user?.profile.showProjectOnboarding &&
        !this.isTeamAccount &&
        this.showUpgradeBar
    },

    showBackdrop () {
      return this.showSidebar && this.isMobile
    },

    isPaperShape () {
      return this.settings.mode === 'Paper shape'
    },

    isBooksPage () {
      return this.$route.name === 'Books' || this.$route.name === 'TeamBooks'
    },

    isBookPage () {
      return this.$route.name === 'Book' || this.$route.name === 'TeamBook' || this.$route.name === 'Headquarters'
    },

    isHeadquarters () {
      return this.$route.name === 'Headquarters'
    },

    isWeekPlanner () {
      return this.$route.name === 'WeekPlanner' || this.$route.name === 'WeekPlannerTeam'
    },

    projectInstructionsActiveFirstStep () {
      return !this.user?.profile.showProjectOnboarding && this.projectInstructionsActiveStep === 1
    },

    projectInstructionsActiveSecondStep () {
      return !this.user?.profile.showProjectOnboarding && this.projectInstructionsActiveStep === 2
    },

    homeClasses () {
      let classes
      if ((!this.user?.profile.showProjectOnboarding && this.isBookPage && this.projectInstructionsActiveStep === 2)) {
        classes = 'bg-onboarding-backdrop'
      }

      if (this.user?.profile.showBookOnboarding && this.isPoseidonTheme && this.isBookPage) {
        classes = 'bg-white'
      } else if (this.user?.profile.showBookOnboarding && this.isPoseidonTheme && (this.$route.name !== 'Book' || this.$route.name !== 'TeamBook')) {
        classes = 'bg-notebooks-poseidon'
      }
      return classes
    },

    mainClasses () {
      let classes
      if (this.user?.profile.showProjectOnboarding) {
        if (this.isPoseidonTheme) {
          classes = this.isBookPage && this.isPaperShape ? 'bg-primary' : ''
        } else {
          classes = this.isDarkMode ? 'bg-canvas' : 'bg-primary'
        }
      } else {
        classes = ''
      }
      return classes
    }
  },

  watch: {
    $route (to) {
      if (!this.isMobile && (to.name === 'Books' || to.name === 'TeamBooks')) this.updateSidebar(true)
    },

    projectInstructionsActiveFirstStep (value) {
      if (value) preventBodyScroll(true)
    },

    showSidebar (value) {
      if (this.isMobile) preventBodyScroll(value)
    },

    isMobile (value) {
      this.updateSidebar(!value)
    }
  },

  created () {
    // const stripe = window.Stripe(process.env.VUE_APP_STRIPE)
    // stripe.redirectToCheckout({ sessionId: 'cs_test_a1CWFFBGftgXqQ5azc4Ozu20vzcQ5ET5jRTsVAFHPY4jmOdGhAQpnsR3qu' })
    //   .then(() => {})
    //   .catch(() => this.loading(false))
    const planId = localStorageService.getItem('planId')

    if (planId) {
      this.updatePlan(planId)
    }

    if (this.isMobile || this.isWeekPlanner) {
      this.updateSidebar(false)
    } else {
      this.updateSidebar(true)
    }
  },

  async mounted () {
    const team = await this.getTeam()
    const user = await this.getProfile()

    if (this.$route.name === 'Books' || this.$route.name === 'Book' || this.$route.name === 'TaskBoard' || this.$route.name === 'WeekPlanner') {
      if (!user.profile.hasPersonal) {
        this.$router.push({ name: 'TeamBooks', params: { team: this.team.link } })
      } else {
        this.setTeamId(null)
      }
    } else if (this.$route.name === 'TeamBooks' || this.$route.name === 'TeamBook' || this.$route.name === 'TaskBoardTeam' || this.$route.name === 'WeekPlannerTeam') {
      if (!team) {
        this.$router.push({ name: 'Books' })
      } else if (this.$route.params.team !== team.link) {
        this.$router.push({ name: 'TeamBooks', params: { team: this.team.link } })
      }
    }

    await this.getGlobalData()

    this.loaded = true
    this.autoSwitchingTheme()

    if (this.token && this.loaded) {
      const page = localStorageService.getItem('lastOpenedProject')
      if (page) {
        if (this.isTeamAccount) {
          this.$router.push({
            name: 'TeamBook',
            params: { team: this.team.link, id: page.bookId },
            query: { page: page.projectId }
          })
        } else {
          this.$router.push(
            {
              name: 'Book',
              params: { id: page.bookId },
              query: { page: page.projectId }
            })
            .catch(() => {})
        }
      }
    }

    document.addEventListener('keydown', this.hotKeys)
    document.addEventListener('mousedown', this.mouseDown)
  },

  beforeUnmount () {
    document.removeEventListener('keydown', this.hotKeys)
    document.removeEventListener('mousedown', this.mouseDown)
    window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', this.toggleTheme)
  },

  methods: {
    ...mapMutations([
      'updateSidebar',
      'updateSettings',
      'updatePreviousTheme',
      'changeActiveState',
      'toggleSettingsModal',
      'loading',
      'updatePreviousDarkTheme',
      'setTeamId'
    ]),

    ...mapActions(['getGlobalData', 'getCurrentPlan', 'upgradePlan', 'getTeam', 'getProfile']),

    async updatePlan (id) {
      localStorageService.removeItem('planId')
      const currentPlan = await this.getCurrentPlan()

      if (currentPlan.planId === +this.$route.query.planId || currentPlan.planId === 9 || currentPlan.planId === 10) {
        this.$router.push({ name: 'Books' })
      } else {
        this.upgradePlan({
          planId: id,
          userId: this.user?.id
        }).then(res => {
          this.loading(true)

          const stripe = window.Stripe(process.env.VUE_APP_STRIPE)
          stripe.redirectToCheckout({ sessionId: res })
            .then(() => {})
            .catch(() => this.loading(false))
        })
      }
    },

    hotKeys (event) {
      if (event.ctrlKey || event.metaKey) {
        // ctrl + 0 / cmd + 0
        if (event.keyCode === 48) {
          this.changeActiveState('Keyboard')
          this.toggleSettingsModal(true)
        }
        // ctrl + ? / cmd + ?
        if (event.keyCode === 191) {
          this.updateDesign()
        }
      }
    },

    autoSwitchingTheme () {
      const systemDarkTheme = window.matchMedia('(prefers-color-scheme: dark)')

      systemDarkTheme.addEventListener('change', this.toggleTheme)
    },

    toggleTheme (e) {
      if (this.isAutoSwitchingThemeEnable) {
        if ((e.matches && !this.isDarkMode) || (!e.matches && this.isDarkMode)) {
          this.updateDesign()
        }
      }
    },

    mouseDown () {
      if (window.getSelection) {
        window.getSelection().removeAllRanges()
      }
    },

    updateDesign () {
      let theme = ''
      const previousThemes = localStorageService.getItem('previous-themes') || {}
      const userPreviousThemes = previousThemes[this.user?.id] || {}

      if ((!this.settings.theme.includes('Dark')) && (this.settings.theme !== 'Dracula')) {
        theme = this.previousDarkTheme || userPreviousThemes?.dark || 'Dark Orange'
        this.updatePreviousTheme(this.settings.theme)
        updatePreviousThemesLocalStorage(this.user, 'light', this.settings.theme)
      } else {
        theme = this.previousTheme || userPreviousThemes?.light || 'Magenta'
        this.updatePreviousDarkTheme(this.settings.theme)
        updatePreviousThemesLocalStorage(this.user, 'dark', this.settings.theme)
      }

      const settings = { ...this.settings, theme: theme }

      this.updateSettings(settings)
      settingsService.update(settings)

      dispatchChangingDesign(settings)
    },

    toggleSidebar () {
      this.updateSidebar(!this.showSidebar)
    }
  }
}
</script>

<style lang="scss" scoped>
@media (min-width: 1320px) {
  .main-wrapper {
    width: calc(50% + 376px);
    left: 0;
    margin-left: auto;
  }
}
</style>
